'use client';

import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import useClientStore from '@/store/useClientStore';
import useVehiclesStore from '@/store/useVehiclesStore';
import { useInitializeStore } from '@/hooks/useInitializeStore';
import { useLanguageStore, isValidLanguage } from '@/store/useLanguageStore';
import { TENANT_ROW_SELECT } from '@/lib/website-config-cols';
import { tomarPrefetch } from '@/lib/prefetch-temprano';
import { ordenDelTenant } from '@/lib/orden-vehiculos';
import { useGeneralStore } from '@/store/useGeneralStore';
import type { Catalogos } from '@/lib/catalogos';
import { hostDeBusqueda } from '@/lib/hostDelTenant';

// elements_structure can grow to 10+ MB per client (LZ-compressed builder JSON).
// We exclude it from the initial fetch so the site renders fast (~5 KB instead
// of multi-MB), then hydrate it in background. BuilderPageWrapper has a
// fallback that renders default templates while elements_structure is missing.
// (Las columnas viven en lib/website-config-cols: las comparte el servidor.)

interface Props {
  children: React.ReactNode;
  /**
   * La fila del tenant ya resuelta por el servidor (layout.tsx, misma consulta
   * que antes hacía este provider al montar). Con ella no hay que volver a
   * pedirla: el store arranca con el tenant y las dos descargas grandes
   * (árbol del builder e inventario) salen de inmediato. Sin ella —p. ej. si
   * la consulta del servidor falló— se pide desde acá, como siempre.
   */
  initialClient?: Record<string, any> | null;
  /**
   * Catálogos globales resueltos en el servidor (lib/catalogos). Se hidratan
   * en `useGeneralStore` ANTES de que `useInitializeStore` corra, así ese
   * hook encuentra el store cargado y no sale a buscarlos. Sin ellos, se
   * piden como siempre.
   */
  initialCatalogs?: Catalogos | null;
}

export function ClientProvider({ children, initialClient = null, initialCatalogs = null }: Props) {
  const { setClient, setIsLoading } = useClientStore();
  const { ensureVehicles } = useVehiclesStore();
  // Va antes de useInitializeStore a propósito: los efectos corren en orden
  // de declaración y éste tiene que dejar el store hidratado primero.
  useEffect(() => {
    if (initialCatalogs) useGeneralStore.getState().hydrateCatalogs(initialCatalogs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { isLoading } = useInitializeStore();
  const { setLanguage } = useLanguageStore();

  useEffect(() => {
    async function loadClientAndVehicles() {
      // {slug}.goauto.lat se busca como {slug}.goauto.cl (ver hostDelTenant).
      const domain = hostDeBusqueda(window.location.host);

      let clientData: Record<string, any> | null = initialClient?.id != null ? initialClient : null;

      if (!clientData) {
        // Match either the GoAuto subdomain (domain) or a client-connected
        // custom domain (custom_domain). Mirrors middleware.ts tenant resolution;
        // without the custom_domain branch, sites on a propio domain 406 here and
        // the loading spinner never clears.
        const { data } = await supabase
          .from('clients')
          .select(TENANT_ROW_SELECT)
          .or(`domain.eq.${domain},custom_domain.eq.${domain}`)
          .single();
        clientData = (data as Record<string, any> | null) ?? null;
      }

      if (clientData) {
        setClient(clientData as any);
        // Fire-and-forget: vehicles fetch joins 10 tables and can take seconds.
        // Don't block the site shell on it — useVehiclesStore.isLoading will keep
        // vehicle-listing components in skeleton state until it lands.
        //
        // La ficha de un vehículo (/vehicles/<id>) no lee el inventario: su
        // vehículo llega por SSR y los similares también. Ahí no se baja; si el
        // visitante navega después al listado o al home, el consumidor lo pide
        // por `useEnsureVehicles` y se carga recién en ese momento.
        if (!/^\/vehicles\/[^/]+/.test(window.location.pathname)) {
          void ensureVehicles(
            clientData.id,
            clientData.has_demo,
            // Ticket #164: el orden inicial que configuró la automotora.
            ordenDelTenant((clientData as Record<string, unknown>).web_vehicle_order)
          );
        }

        // Initialize language from client default if no user preference is persisted
        try {
          const persisted = localStorage.getItem('language-storage');
          const parsed = persisted ? JSON.parse(persisted) : null;
          const existing = parsed?.state?.currentLanguage as string | undefined;
          const clientDefault = (clientData as any)?.default_language as
            | string
            | undefined;
          if (!existing && clientDefault && isValidLanguage(clientDefault)) {
            setLanguage(clientDefault);
          }
        } catch (e) {
          // ignore persistence errors
        }

        setIsLoading(false);

        // Background-hydrate elements_structure (the heavy column). When it lands,
        // BuilderPageWrapper's useMemo on client_website_config recalculates and
        // its cache gets populated, swapping the fallback for the real builder UI.
        const cfgArr = (clientData as any).client_website_config;
        const cfgRow = Array.isArray(cfgArr) ? cfgArr[0] : cfgArr;
        if (cfgRow?.id) {
          // Si el `<head>` ya disparó esta misma consulta (ver
          // lib/prefetch-temprano), se usa esa respuesta; si falló o no
          // existe, se pide como siempre.
          const temprano = tomarPrefetch<Array<{ id: string; elements_structure: unknown }>>(
            'elements_structure',
            String(cfgRow.id)
          );
          const pedir = (): Promise<{ elements_structure?: unknown } | null> =>
            Promise.resolve(
              supabase
                .from('client_website_config')
                .select('id, elements_structure')
                .eq('id', cfgRow.id)
                .single()
            ).then(({ data: esData }) => esData as { elements_structure?: unknown } | null);
          const estructura: Promise<{ elements_structure?: unknown } | null> = temprano
            ? temprano
                .then((filas) => (Array.isArray(filas) ? filas[0] ?? null : null))
                .catch((e) => {
                  console.warn('Prefetch temprano del builder falló; se pide de nuevo:', e);
                  return pedir();
                })
            : pedir();
          estructura
            .then((esData) => {
              if (!esData?.elements_structure) return;
              const current = useClientStore.getState().client;
              if (!current || (current as any).id !== (clientData as any).id) return;
              const currentCfg = (current as any).client_website_config;
              const currentRow = Array.isArray(currentCfg) ? currentCfg[0] : currentCfg;
              if (!currentRow) return;
              const updatedRow = { ...currentRow, elements_structure: esData.elements_structure };
              const updatedCfg = Array.isArray(currentCfg) ? [updatedRow] : updatedRow;
              setClient({ ...current, client_website_config: updatedCfg } as any);
            });
        }
      } else {
        setIsLoading(false);
      }
    }

    loadClientAndVehicles();
    // Corre una sola vez al montar, como siempre: `initialClient` es el tenant
    // del host y no cambia durante la visita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return children;
}
